import type { CodeMigration } from './types.js';
import { appTsxMigration_v2_0_0_preview_381 } from './app-tsx-migration.js';
import { appTsxRetroactiveMigration } from './app-tsx-retroactive-migration.js';
import { indexCssTailwindV4Migration } from './index-css-tailwind-v4-migration.js';
import { programCsAzureMonitorMigration } from './program-cs-azure-monitor-migration.js';
import { smartstackThemePostinstallMigration } from './smartstack-theme-postinstall-migration.js';

/**
 * Registry of all code migrations
 * Migrations are applied in order from oldest to newest
 */
const migrations: CodeMigration[] = [
  appTsxRetroactiveMigration,
  appTsxMigration_v2_0_0_preview_381,
  indexCssTailwindV4Migration,
  programCsAzureMonitorMigration,
  smartstackThemePostinstallMigration,
];

/**
 * Compares two semver versions
 * @returns -1 if a < b, 0 if a === b, 1 if a > b
 */
function compareVersions(a: string, b: string): number {
  // Remove 'v' prefix if present
  const cleanA = a.replace(/^v/, '');
  const cleanB = b.replace(/^v/, '');

  // Split by '-' to separate version from prerelease
  const [versionA, prereleaseA] = cleanA.split('-');
  const [versionB, prereleaseB] = cleanB.split('-');

  // Compare main version parts
  const partsA = versionA.split('.').map(Number);
  const partsB = versionB.split('.').map(Number);

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const partA = partsA[i] || 0;
    const partB = partsB[i] || 0;

    if (partA < partB) return -1;
    if (partA > partB) return 1;
  }

  // If versions are equal, compare prerelease
  if (!prereleaseA && !prereleaseB) return 0;
  if (!prereleaseA) return 1; // Stable version > prerelease
  if (!prereleaseB) return -1; // Prerelease < stable version

  // Both are prereleases, compare them
  if (prereleaseA < prereleaseB) return -1;
  if (prereleaseA > prereleaseB) return 1;
  return 0;
}

/**
 * Finds all migrations that need to be applied between two versions
 * @param fromVersion Current version (e.g., "2.0.0-preview.380")
 * @param toVersion Target version (e.g., "2.0.0-preview.381")
 * @returns Array of migrations to apply, in order
 */
export function findApplicableMigrations(fromVersion: string, toVersion: string): CodeMigration[] {
  return migrations.filter(migration => {
    const currentVsMigrationFrom = compareVersions(fromVersion, migration.fromVersion);
    const currentVsMigrationTo = compareVersions(fromVersion, migration.toVersion);
    const targetVsMigrationTo = compareVersions(toVersion, migration.toVersion);

    // Migration applies if EITHER:
    // 1. Standard upgrade: current <= migration.from AND target >= migration.to
    // 2. Retroactive fix: current >= migration.from AND current <= migration.to
    const isStandardUpgrade = currentVsMigrationFrom <= 0 && targetVsMigrationTo >= 0;
    const isRetroactiveFix = currentVsMigrationFrom >= 0 && currentVsMigrationTo <= 0;

    return isStandardUpgrade || isRetroactiveFix;
  }).sort((a, b) => compareVersions(a.toVersion, b.toVersion));
}

/**
 * Gets all registered migrations
 */
export function getAllMigrations(): CodeMigration[] {
  return [...migrations];
}

/**
 * Registers a new migration (useful for testing or dynamic registration)
 */
export function registerMigration(migration: CodeMigration): void {
  migrations.push(migration);
}
